import {
  NotificationAck,
  NotificationsRealtimePayload,
  NotificationsTransport,
  NotificationsTransportEventMap,
  NotificationsTransportSession,
  NotificationsTransportSyncPayload,
} from '../types/Notifications/types';
import {
  buildNotificationsAckPayload,
  buildNotificationsSyncPayload,
  debugNotificationsLog,
  getNotificationsRealtimeMode,
  getNotificationsRealtimePath,
  getNotificationsRealtimeUrl,
  NOTIFICATIONS_CLIENT_EVENTS,
  NOTIFICATIONS_SERVER_EVENTS,
} from './notificationsProtocol';

type SocketIoLike = {
  connected: boolean;
  on(event: string, callback: (...args: any[]) => void): SocketIoLike;
  emit(event: string, payload?: unknown): SocketIoLike;
  disconnect(): SocketIoLike;
};

const createSocketIoClient = (): ((url: string, options: Record<string, unknown>) => SocketIoLike) =>
  require('socket.io-client');

type ListenerMap = {
  [EventName in keyof NotificationsTransportEventMap]: Set<
    (payload: NotificationsTransportEventMap[EventName]) => void
  >;
};

const createListenerMap = (): ListenerMap => ({
  connected: new Set(),
  disconnected: new Set(),
  error: new Set(),
  notification: new Set(),
  sync: new Set(),
});

const emit = <EventName extends keyof NotificationsTransportEventMap>(
  listeners: ListenerMap,
  event: EventName,
  payload: NotificationsTransportEventMap[EventName],
) => {
  listeners[event].forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error(`[NotificationsTransport] Error in listener for ${event}`, error);
    }
  });
};

export const createSocketIoNotificationsTransport = (url: string): NotificationsTransport => {
  const listeners = createListenerMap();
  let socket: SocketIoLike | null = null;
  const path = getNotificationsRealtimePath();

  return {
    async connect(session: NotificationsTransportSession) {
      const io = createSocketIoClient();

      debugNotificationsLog('Conectando socket.io de notificaciones', { url, path });
      socket = io(url, {
        path,
        transports: ['polling', 'websocket'],
        upgrade: true,
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1500,
        timeout: 10000,
        query: {
          token: session.token,
        },
      });

      socket.on('connect', () => {
        debugNotificationsLog('Websocket de notificaciones conectado');
        emit(listeners, 'connected', undefined);
      });

      socket.on('disconnect', (reason?: string) => {
        debugNotificationsLog('Websocket de notificaciones desconectado', {
          reason: reason || 'socket_closed',
        });
        emit(listeners, 'disconnected', { reason: reason || 'socket_closed' });
      });

      socket.on('connect_error', (error?: Error) => {
        const detailedError = {
          message: error?.message || 'unknown',
          description: (error as Error & { description?: unknown })?.description,
          type: (error as Error & { type?: unknown })?.type,
          url,
          path,
        };
        debugNotificationsLog('Error de websocket de notificaciones', detailedError);
        emit(listeners, 'error', { message: 'No se pudo conectar el websocket' });
      });

      socket.on('reconnect_attempt', (attempt?: number) => {
        debugNotificationsLog('Reintentando websocket de notificaciones', {
          attempt: attempt ?? 0,
          url,
          path,
        });
      });

      socket.on(NOTIFICATIONS_SERVER_EVENTS.notification, (payload: NotificationsRealtimePayload) => {
        debugNotificationsLog('Mensaje websocket recibido', payload);
        emit(listeners, 'notification', payload);
      });

      socket.on(NOTIFICATIONS_SERVER_EVENTS.syncResult, (items: NotificationsRealtimePayload[]) => {
        debugNotificationsLog('Resultado de sync recibido', items);
        const normalizedItems = Array.isArray(items) ? items : [];
        emit(listeners, 'sync', {
          items: normalizedItems,
          cursor: normalizedItems[0]?.id ? String(normalizedItems[0].id) : null,
        } as NotificationsTransportSyncPayload);
      });
    },
    async disconnect() {
      socket?.disconnect();
      socket = null;
    },
    async sync(cursor?: string | null) {
      const payload = buildNotificationsSyncPayload(cursor);
      debugNotificationsLog('Enviando sync de notificaciones', payload);
      socket?.emit(NOTIFICATIONS_CLIENT_EVENTS.sync, payload);
    },
    async sendAck(ack: NotificationAck) {
      const payload = buildNotificationsAckPayload(ack);
      debugNotificationsLog('Enviando ACK de notificaciones', {
        event: NOTIFICATIONS_CLIENT_EVENTS[ack.type],
        payload,
      });
      socket?.emit(NOTIFICATIONS_CLIENT_EVENTS[ack.type], payload);
    },
    on(event, callback) {
      listeners[event].add(callback);
      return () => {
        listeners[event].delete(callback);
      };
    },
  };
};

export const createNotificationsTransport = (): NotificationsTransport => {
  const websocketUrl = getNotificationsRealtimeUrl();
  const realtimeMode = getNotificationsRealtimeMode();

  if (websocketUrl && realtimeMode === 'websocket' && typeof WebSocket !== 'undefined') {
    return createSocketIoNotificationsTransport(websocketUrl);
  }

  return {
    async connect() {
      debugNotificationsLog('Realtime deshabilitado: falta URL o soporte websocket');
    },
    async disconnect() {
      // noop
    },
    async sync() {
      // noop
    },
    async sendAck() {
      // noop
    },
    on() {
      return () => undefined;
    },
  };
};
